import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBlockNavigation } from 'providers';
import {
    useBoolean,
    useBack,
    useEditTemplate,
    useDeleteTemplates,
    useGetTemplate
} from 'hooks';
import { Template as TemplateData, CreateTemplateData } from 'types/template';
import {
    ActionButton,
    Box,
    Toolbar,
    ToolbarGroup,
    ModalDeleteDialog,
    Typography,
    ScrollableFlexContainer,
    Divider,
    Delete,
    ArrowPointerLeft,
    Save,
    LoadingIndicator
} from 'components';
import AbstractTemplate from './shared/AbstractTemplate';
import { ApplicationListItems } from './shared/ApplicationList';
import { useNotification } from 'libs/notification';

interface RouteParams {
    name: string;
}

const Template: FC = () => {
    const { t } = useTranslation();
    const { name } = useParams<RouteParams>();
    const decodedName = useMemo(() => decodeURIComponent(name), [name]);
    const goBack = useBack(name);
    const { success, error } = useNotification();
    const history = useHistory();
    const [isOpen, show, hide] = useBoolean();
    const [hasError, setHasError] = useState(false);

    const { mutate: edit } = useEditTemplate();
    const { mutate: deleteTemplate } = useDeleteTemplates();
    const { data: template, isLoading } = useGetTemplate(decodedName);

    const [blacklist, setBlacklist] = useState<ApplicationListItems>({
        isEnabled: template?.blacklistEnabled ?? false,
        items: template?.blacklist ?? []
    });
    const [whitelist, setWhitelist] = useState<ApplicationListItems>({
        isEnabled: template?.whitelistEnabled ?? false,
        items: template?.whitelist ?? []
    });

    const [templateDetails, setTemplateDetails] = useState<TemplateData>();

    useEffect(() => {
        if (template) {
            setTemplateDetails(template);
            setBlacklist({
                isEnabled: template.blacklistEnabled,
                items: template?.blacklist ?? []
            });
            setWhitelist({
                isEnabled: template.whitelistEnabled,
                items: template?.whitelist ?? []
            });
        }
    }, [template]);

    const { setIsNavigationBlocked } = useBlockNavigation();

    const handleSaveTemplate = () => {
        setIsNavigationBlocked(false);

        const calculatedTemplateDetails: CreateTemplateData = {
            ...templateDetails,
            whitelistEnabled: whitelist.isEnabled,
            blacklistEnabled: blacklist.isEnabled,
            whitelist: whitelist.items,
            blacklist: blacklist.items,
            fieldRedactionEnabled: templateDetails.fieldRedactionEnabled,
            transformations: templateDetails.transformations
        };

        edit(
            {
                originalName: decodedName,
                template: calculatedTemplateDetails
            },
            {
                onSuccess: (response) => {
                    success(
                        t('expressions.template_saved', {
                            name: template.name
                        })
                    );

                    if (response.data.name !== decodedName) {
                        history.replace(
                            `/templates/${encodeURIComponent(
                                response.data.name
                            )}`
                        );
                    }
                },
                onError: () => {
                    error(
                        t('error.could_not_save_template', {
                            templateName: decodedName
                        })
                    );
                }
            }
        );
    };

    const onDeleteTemplate = () => {
        deleteTemplate([template], {
            onSuccess: () => {
                setIsNavigationBlocked(false);
                history.replace('/templates');
                success(
                    t('expressions.delete_template_success', {
                        name: template.name
                    })
                );
            },
            onError: () => {
                error(
                    t('error.could_not_delete_template', { templateName: name })
                );
            }
        });
    };

    return (
        <>
            <ModalDeleteDialog
                isOpen={isOpen}
                titleText={t('expressions.delete_template')}
                onClose={hide}
                onDelete={onDeleteTemplate}>
                <Typography variant="body1">
                    {t('delete_modal.delete_item')}
                </Typography>
            </ModalDeleteDialog>
            <ScrollableFlexContainer>
                <LoadingIndicator />
                {!isLoading && templateDetails && (
                    <>
                        <Box m={2}>
                            <Toolbar isRounded={false}>
                                <ToolbarGroup align="left">
                                    <ActionButton
                                        icon={<ArrowPointerLeft />}
                                        onClick={goBack}>
                                        {t('general.Back')}
                                    </ActionButton>
                                </ToolbarGroup>
                                <ToolbarGroup align="right">
                                    <ToolbarGroup>
                                        <ActionButton
                                            onClick={show}
                                            icon={
                                                <Delete
                                                    color={
                                                        template.default ||
                                                        templateDetails.readOnly
                                                            ? 'disabled'
                                                            : 'error'
                                                    }
                                                />
                                            }
                                            isDisabled={
                                                template.default ||
                                                templateDetails.readOnly
                                            }>
                                            {t('expressions.delete_template')}
                                        </ActionButton>
                                    </ToolbarGroup>
                                    <Divider spacing={0} height={22} />
                                    <ActionButton
                                        onClick={handleSaveTemplate}
                                        icon={
                                            <Save
                                                color={
                                                    templateDetails.readOnly ||
                                                    hasError
                                                        ? 'disabled'
                                                        : 'primary'
                                                }
                                            />
                                        }
                                        isDisabled={
                                            templateDetails.readOnly || hasError
                                        }>
                                        {t('expressions.save_template')}
                                    </ActionButton>
                                </ToolbarGroup>
                            </Toolbar>
                        </Box>
                        <AbstractTemplate
                            isReadOnly={templateDetails.readOnly}
                            template={templateDetails}
                            templateSetter={setTemplateDetails}
                            blacklist={blacklist}
                            blacklistSetter={setBlacklist}
                            whitelist={whitelist}
                            whitelistSetter={setWhitelist}
                            setHasError={setHasError}
                        />
                    </>
                )}
            </ScrollableFlexContainer>
        </>
    );
};

export default Template;
